'use client';

import React, { useMemo } from 'react';

import { COPY } from '@/shared/constants/copies';
import { ISPlanUsage } from '@/shared/services/payment/payment.types';

import { cn } from '@/lib/utils';

type UsageMeterProps = {
	usage: ISPlanUsage;
	className?: string;
};

/**
 * One combined credits meter.
 *
 * `usage.limit` already includes any top-up, so plan and top-up are never shown as
 * separate meters — the API contract requires a single bar.
 */
export const UsageMeter = React.memo(function UsageMeter({ usage, className }: UsageMeterProps) {
	const percentUsed = useMemo(() => {
		if (usage.limit <= 0) return 0;
		return Math.min(100, Math.max(0, (usage.used / usage.limit) * 100));
	}, [usage.used, usage.limit]);

	const isExhausted = usage.remaining <= 0;
	const isNearLimit = !isExhausted && percentUsed >= 80;

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			<div className="flex items-baseline justify-between gap-3">
				<span className="text-sm font-medium text-primary">
					{COPY.billing.usageUsed(usage.used)}
				</span>
				<span className="text-xs text-secondary">
					{COPY.billing.usageResets(usage.resetAt)}
				</span>
			</div>

			<div
				className="h-2 w-full overflow-hidden rounded-full bg-muted"
				role="progressbar"
				aria-valuemin={0}
				aria-valuemax={usage.limit}
				aria-valuenow={usage.used}
				aria-label={COPY.billing.usageLabel}
			>
				<div
					className={cn(
						'h-full rounded-full transition-all',
						isExhausted ? 'bg-destructive' : isNearLimit ? 'bg-amber-500' : 'bg-primary'
					)}
					style={{ width: `${percentUsed}%` }}
				/>
			</div>
		</div>
	);
});
